/**
 * 🐛 [Phase v13.48 — 2026-07-14] Composant `PlanificationApproxSection`
 *
 * Section UI de saisie de la planification approximative d'un BT (Sujet 4.B).
 * S'insère au-dessus de la section « Dates de chantier » dans les 2 dialogs
 * (Créer + Éditer).
 *
 * Comportement :
 *   - Replié par défaut si `planificationApprox === null` — bouton
 *     « + Ajouter une période approximative ».
 *   - Déplié : 4 champs (Mois, Année, Portion, Durée) + dropdown Chef.
 *   - Badge ambre « brouillon » quand `datesChantierOfficielles.length > 0`.
 *   - Aucun bouton « Enregistrer » séparé : le parent déclenche PUT/DELETE
 *     dans son propre `doSave()`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Trash2, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOIS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const PORTIONS = [
  { value: 'debut', label: 'Début (1-10)' },
  { value: 'milieu', label: 'Milieu (11-20)' },
  { value: 'fin', label: 'Fin (21-31)' },
];

/**
 * Filtre les employés qui peuvent être chef d'équipe (mêmes règles que Phase v13.46).
 */
function isChefCandidate(e) {
// Cat H — `e.role` = rôle de l'employé assigné dans les données du BT (pas rôle du user connecté). Filtre de rendu (couronne owner), pas RBAC.
  const isProprio = e.est_proprietaire === true || e.role === 'proprietaire';  // E3b-whitelist : drapeau est_proprietaire de la fiche (donnée)
  const isChef = e.type_employe === 'chef_equipe' || e.role_terrain === 'chef_equipe' || isProprio;
  const isVisible = e.est_actif !== false || isProprio;
  return isVisible && isChef;
}

/**
 * Retourne les bornes calendaires (debut, fin) d'une portion.
 * Réplique de `get_portion_dates` côté frontend (indépendant du backend).
 */
function getPortionDates(annee, mois, portion) {
  const dernierJour = new Date(annee, mois, 0).getDate(); // mois 1-indexé, day 0 = dernier jour du mois précédent
  if (portion === 'debut') return [new Date(annee, mois - 1, 1), new Date(annee, mois - 1, 10)];
  if (portion === 'milieu') return [new Date(annee, mois - 1, 11), new Date(annee, mois - 1, 20)];
  return [new Date(annee, mois - 1, 21), new Date(annee, mois - 1, dernierJour)];
}

/**
 * Compte les jours ouvrables (hors weekends et fériés QC) entre 2 dates (inclusif).
 */
function compterJoursOuvrables(debut, fin, feriesSet) {
  let n = 0;
  const d = new Date(debut);
  while (d <= fin) {
    const day = d.getDay();
    const iso = d.toISOString().slice(0, 10);
    if (day !== 0 && day !== 6 && !feriesSet.has(iso)) n += 1;
    d.setDate(d.getDate() + 1);
  }
  return n;
}


export function PlanificationApproxSection({
  bonId = null,
  planificationApprox = null,
  onChange,
  datesChantierOfficielles = [],
  employees = [],
  disabled = false,
}) {
  const [deplie, setDeplie] = useState(planificationApprox !== null);
  const [feries, setFeries] = useState({}); // { 2026: Set<'YYYY-MM-DD'>, ... }

  const anneeCourante = new Date().getFullYear();
  const anneesDisponibles = [anneeCourante, anneeCourante + 1, anneeCourante + 2];

  // ─── Valeurs contrôlées (défauts si null / création vierge) ──────────
  const draft = planificationApprox || {
    mois: new Date().getMonth() + 1,
    annee: anneeCourante,
    portion: 'debut',
    duree_jours_ouvrables: 5,
    chef_id: null,
  };

  // ─── Fetch fériés QC pour l'année active (lazy, une fois par année) ──
  useEffect(() => {
    if (!deplie) return;
    const annee = draft.annee;
    if (feries[annee]) return;
    axios.get(`${API_URL}/api/crm/jours-feries-qc?annee=${annee}`)
      .then(r => {
        const set = new Set((r.data?.jours_feries || []).map(f => f.date));
        setFeries(prev => ({ ...prev, [annee]: set }));
      })
      .catch(() => {
        // silencieux : le comptage marchera sans les fériés (juste weekends)
        setFeries(prev => ({ ...prev, [annee]: new Set() }));
      });
  }, [deplie, draft.annee, feries]);

  const enBrouillon = datesChantierOfficielles.length > 0;

  const chefs = useMemo(() => employees.filter(isChefCandidate), [employees]);

  const infoPortion = useMemo(() => {
    const [debut, fin] = getPortionDates(draft.annee, draft.mois, draft.portion);
    const set = feries[draft.annee] || new Set();
    const n = compterJoursOuvrables(debut, fin, set);
    const fmt = (d) => `${d.getDate()}${d.getDate() === 1 ? 'er' : ''} ${MOIS_FR[d.getMonth()].toLowerCase()}`;
    return { texte: `${fmt(debut)} au ${fmt(fin)} ${draft.annee}`, jours: n };
  }, [draft.annee, draft.mois, draft.portion, feries]);

  // ─── 🚀 [Phase v13.49 — 2026-07-14] Aperçu placement + suggestions (Sujet 4.C)
  //     Debounce 500ms sur les changements de voeu, appel POST /calculer, rendu de
  //     l'aperçu sous les champs. Ne persiste rien (l'utilisatrice décide au save).
  const [apercu, setApercu] = useState(null);
  const [chargeApercu, setChargeApercu] = useState(false);

  useEffect(() => {
    if (!deplie) return;
    const btIdEffectif = bonId || 'nouveau';
    const controller = new AbortController();
    setChargeApercu(true);
    const timer = setTimeout(() => {
      axios.post(
        `${API_URL}/api/crm/bons-travail/${btIdEffectif}/planification-approx/calculer`,
        {
          mois: draft.mois,
          annee: draft.annee,
          portion: draft.portion,
          duree_jours_ouvrables: draft.duree_jours_ouvrables,
          chef_id: draft.chef_id || null,
        },
        { signal: controller.signal },
      )
        .then(r => setApercu(r.data))
        .catch(() => setApercu(null))
        .finally(() => setChargeApercu(false));
    }, 500);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [deplie, bonId, draft.mois, draft.annee, draft.portion, draft.duree_jours_ouvrables, draft.chef_id]);

  const accepterAlternative = (dates) => {
    // Applique les dates alternatives en gardant mois/portion pertinents.
    if (!dates || dates.length === 0) return;
    // Extrait mois/année de la 1re date : YYYY-MM-DD
    const [y, m] = dates[0].split('-').map(Number);
    // Détermine la portion depuis le jour de la 1re date
    const day = parseInt(dates[0].slice(-2), 10);
    const nouvellePortion = day <= 10 ? 'debut' : (day <= 20 ? 'milieu' : 'fin');
    onChange({
      ...draft,
      annee: y,
      mois: m,
      portion: nouvellePortion,
    });
  };

  // ─── Handlers ────────────────────────────────────────────────────────
  const updateField = (field, value) => {
    onChange({ ...draft, [field]: value });
  };

  const handleAjouter = () => {
    setDeplie(true);
    onChange(draft); // initialise avec les défauts
  };

  const handleSupprimer = () => {
    setDeplie(false);
    onChange(null);
  };

  // ─── Rendu ───────────────────────────────────────────────────────────
  return (
    <Card
      data-testid="planification-approx-section"
      className={enBrouillon ? 'border-slate-200 bg-slate-50/40' : 'border-slate-200'}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          📅 Planification approximative
          {planificationApprox && (
            <span className="text-xs font-normal text-slate-500">
              — Réservez une période approximative pour ce chantier
            </span>
          )}
        </CardTitle>
        {!planificationApprox && (
          <p className="text-xs text-slate-500 mt-1">
            Ces dates ne remplacent pas les dates officielles — elles servent à préparer la charge de travail des chefs d'équipe.
          </p>
        )}
      </CardHeader>

      <CardContent>
        {enBrouillon && (
          <div
            className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="planification-approx-brouillon-badge"
          >
            ⚠️ Des dates officielles sont saisies pour ce chantier — la planification approximative est actuellement masquée du calendrier de production.
          </div>
        )}

        {!deplie ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAjouter}
            disabled={disabled}
            data-testid="planification-approx-toggle-btn"
            className="w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter une période approximative
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Mois</Label>
                <Select
                  value={String(draft.mois)}
                  onValueChange={(v) => updateField('mois', parseInt(v, 10))}
                  disabled={disabled}
                >
                  <SelectTrigger data-testid="planification-approx-mois-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MOIS_FR.map((nom, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Année</Label>
                <Select
                  value={String(draft.annee)}
                  onValueChange={(v) => updateField('annee', parseInt(v, 10))}
                  disabled={disabled}
                >
                  <SelectTrigger data-testid="planification-approx-annee-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {anneesDisponibles.map(a => (
                      <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Portion du mois</Label>
              <RadioGroup
                value={draft.portion}
                onValueChange={(v) => updateField('portion', v)}
                disabled={disabled}
                data-testid="planification-approx-portion-radio"
                className="flex flex-col sm:flex-row gap-3 mt-1"
              >
                {PORTIONS.map(p => (
                  <label
                    key={p.value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                    data-testid={`planification-approx-portion-${p.value}`}
                  >
                    <RadioGroupItem value={p.value} />
                    {p.label}
                  </label>
                ))}
              </RadioGroup>
              <p className="text-[11px] text-slate-500 italic mt-1">
                💡 {infoPortion.texte} : <strong>{infoPortion.jours} jour(s) ouvrable(s)</strong> disponibles (weekends & fériés QC exclus)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Durée (jours ouvrables)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={draft.duree_jours_ouvrables}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      updateField('duree_jours_ouvrables', Number.isFinite(v) ? Math.max(1, Math.min(30, v)) : 1);
                    }}
                    disabled={disabled}
                    data-testid="planification-approx-duree-input"
                    className="w-24"
                  />
                  <span className="text-xs text-slate-500">
                    jour(s) — min 1, max 30
                  </span>
                </div>
              </div>

              <div>
                <Label className="text-xs">Chef d'équipe</Label>
                <Select
                  value={draft.chef_id || '__none__'}
                  onValueChange={(v) => updateField('chef_id', v === '__none__' ? null : v)}
                  disabled={disabled}
                >
                  <SelectTrigger data-testid="planification-approx-chef-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" data-testid="planification-approx-chef-sans-chef">
                      — Sans chef —
                    </SelectItem>
                    {chefs.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-400 italic">
                        Aucun chef d'équipe disponible
                      </div>
                    ) : (
                      chefs.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.prenom} {emp.nom}
                          {(emp.est_proprietaire || emp.role === 'proprietaire') ? ' 👑' : ''}  // E3b-whitelist : drapeau est_proprietaire de la fiche (donnée)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-100">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSupprimer}
                disabled={disabled}
                data-testid="planification-approx-delete-btn"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Supprimer la période approximative
              </Button>
            </div>

            {/* 🚀 [Phase v13.49 — 2026-07-14] Aperçu placement + alertes/suggestions (Sujet 4.C) */}
            {apercu && (
              <div className="mt-3 border-t border-slate-100 pt-3" data-testid="planification-approx-apercu">
                {apercu.status === 'ok' && apercu.dates_proposees.length > 0 && (
                  <div
                    className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
                    data-testid="planification-approx-apercu-ok"
                  >
                    <strong>Dates proposées</strong> : {apercu.dates_proposees.slice(0, 8).join(', ')}
                    {apercu.dates_proposees.length > 8 && ` … (+${apercu.dates_proposees.length - 8})`}
                    <span className="text-emerald-600 ml-1">
                      ({apercu.dates_proposees.length} jour{apercu.dates_proposees.length > 1 ? 's' : ''} ouvrable{apercu.dates_proposees.length > 1 ? 's' : ''})
                    </span>
                  </div>
                )}

                {apercu.alertes && apercu.alertes.length > 0 && apercu.alertes.map((a, idx) => (
                  <div
                    key={`alerte-${idx}`}
                    className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 mt-2 flex items-start gap-2"
                    data-testid={`planification-approx-alerte-${idx}`}
                  >
                    <span>⚠️</span>
                    <div className="flex-1">
                      <div>{a.message_fr}</div>
                      {a.dates_alternatives && a.dates_alternatives.length > 0 && (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-amber-900 underline mt-1"
                          onClick={() => accepterAlternative(a.dates_alternatives)}
                          data-testid={`planification-approx-alerte-accept-${idx}`}
                        >
                          Accepter la période alternative
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                {apercu.suggestions_alternatives && apercu.suggestions_alternatives.length > 0 && apercu.suggestions_alternatives.map((s, idx) => (
                  <div
                    key={`suggestion-${idx}`}
                    className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800 mt-2 flex items-start gap-2"
                    data-testid={`planification-approx-suggestion-${idx}`}
                  >
                    <span>💡</span>
                    <div className="flex-1">
                      <div>{s.message_fr}</div>
                      {s.dates_alternatives && s.dates_alternatives.length > 0 && (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-sky-900 underline mt-1"
                          onClick={() => accepterAlternative(s.dates_alternatives)}
                          data-testid={`planification-approx-suggestion-accept-${idx}`}
                        >
                          Accepter la suggestion
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                {apercu.status === 'impossible' && (
                  <div
                    className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 mt-2"
                    data-testid="planification-approx-impossible"
                  >
                    ❌ Aucune période trouvée dans les 3 prochains mois pour ce chef. Considérez « Sans chef » ou modifiez la durée.
                  </div>
                )}
              </div>
            )}
            {chargeApercu && !apercu && (
              <div className="text-xs text-slate-400 italic mt-2" data-testid="planification-approx-apercu-loading">
                Calcul du placement en cours...
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Helper de persistance — appelé depuis `doSave()` du parent après la
 * sauvegarde principale du BT (l'`id` doit exister).
 *
 * @param {string} btId         id du BT (obligatoire — post-création si BT vierge)
 * @param {object|null} planif  objet planification (null → DELETE)
 * @param {object|null} previous  planification précédente (utile pour savoir si un DELETE est requis)
 * @returns {Promise<void>}
 */
export async function persistPlanificationApprox(btId, planif, previous = null) {
  if (!btId) return;
  if (planif === null) {
    if (previous) {
      await axios.delete(`${API_URL}/api/crm/bons-travail/${btId}/planification-approx`);
    }
    return;
  }
  await axios.put(`${API_URL}/api/crm/bons-travail/${btId}/planification-approx`, {
    mois: planif.mois,
    annee: planif.annee,
    portion: planif.portion,
    duree_jours_ouvrables: planif.duree_jours_ouvrables,
    chef_id: planif.chef_id || null,
  });
}


export default PlanificationApproxSection;
